import React, { useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLessonCompletion } from '../../hooks/use-lesson-completion';
import { LoadingSpinner, ErrorState } from '../shared/components';
import LessonTopBar from './components/LessonTopBar';
import StartScreen from './components/StartScreen';
import NavigationBar from './components/NavigationBar';
import CompletionScreen from './components/CompletionScreen';
import { ParticipantsSlide } from './components/ParticipantsSlide';
import { CampParticipanti } from './components/CampParticipanti';
import { EcranClase } from './components/EcranClase';
import { slideRegistry } from '../../core/plugin-system/SlideRegistry';
import BgContentWrap from '../../../../components/layout/bg-content-wrap';
import { useRoutesProvider, useAuthProviderV2 } from '../../../../providers/provider-hooks';
import { getLessonListingPath } from './lesson-exit-path';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { useConfirmationDialog } from '@/shared/hooks/useConfirmationDialog';

interface LessonPlayerProps {
  lessonId: string;
  lessonType: string;
  userId?: string;
  isPreview?: boolean;
  /**
   * Plugin-urile de slide sunt inregistrate (`LessonSystemRouter`). Cat timp nu sunt, un slide
   * fara plugin inseamna „inca se incarca", nu „slide invalid". Implicit `true`, pentru
   * consumatorii care inregistreaza singuri plugin-urile inainte de montare.
   */
  pluginsReady?: boolean;
}

export const LessonPlayer: React.FC<LessonPlayerProps> = ({
  lessonId,
  lessonType,
  userId,
  isPreview = false,
  pluginsReady = true,
}) => {
  // C1 (final-review.md): ✕ trebuie sa iasa la listing-ul PROPRIU tipului de lectie, nu la
  // /lectii (DBEST/ADMIN-only). LessonTopBar isi pastreaza implicitul (LESSON_QUIZ_LISTING) —
  // contractul componentei nu se schimba — dar LessonPlayer stie tipul, deci ii da onExit explicit.
  // Garda de rol (re-review): mapping-ul tip->listing nu garanteaza singur accesul — LESSON
  // permite PROFESOR la completare, dar nu si la listing-ul lui (DBEST/ADMIN). getLessonListingPath
  // verifica ALLOWED_ROLES din NAV_LINKS_V2 si cade pe home-ul rolului cand listing-ul nu-l include.
  const navigate = useNavigate();
  const { PATHS, PAGES, NAV_LINKS_V2, getUserHomePage } = useRoutesProvider();
  const { userInfo } = useAuthProviderV2();
  const userRole = userInfo?.role;
  // `getUserHomePage` din provider e tipat strict pe `Role`; helper-ul de mai jos ramane
  // permisiv pe `string`, ca sa fie testabil si cu roluri necunoscute/invalide. Adaptor la graniță.
  const getUserHomePageForExit = useCallback(
    (role?: string) => getUserHomePage(role as Parameters<typeof getUserHomePage>[0]),
    [getUserHomePage]
  );
  const handleExit = useCallback(
    () => navigate(getLessonListingPath(lessonType, userRole, { PATHS, PAGES, NAV_LINKS_V2, getUserHomePage: getUserHomePageForExit })),
    [navigate, lessonType, userRole, PATHS, PAGES, NAV_LINKS_V2, getUserHomePageForExit]
  );

  // Call the hook with positional parameters
  // In preview mode, userId can be undefined (answers query will be skipped)
  const hookResult = useLessonCompletion(
    lessonId,
    userId,
    isPreview
  );

  // Adapt new hook return to expected LessonPlayer interface
  const {
    // State
    lesson,
    currentSlide,
    currentAnswer,
    answers,
    progress: _progress,
    currentSlideIndex,
    isFirstSlide,
    isLastSlide,
    canGoNext: _canGoNext,
    canGoPrevious,

    // Status
    isLoading,
    isReady,
    isSubmitting,
    isCompleted,
    isError,

    // Resume tracking
    isResumed,

    // CLASS_EXERCISE participants
    isParticipantsSlide,
    campParticipantiPePagina,
    participants,
    setParticipants,
    participantiIncercat,

    // SP-05a — ecranul „care clase" al activitatii practice
    isClaseSlide,
    clase,
    setClase,
    fragmenteRespinse,
    claseDejaPunctate,
    aTrimisAcum,

    // Actions
    updateAnswer,
    validateCurrentAnswer: _validateCurrentAnswer,
    submitCurrentAnswer,
    previousSlide,
    restart,
    start,

    // Config
    config,
  } = hookResult;


  // Adapter: map new properties to old ones for backward compatibility
  const slideAnswers = answers;
  const lessonAnswer = null; // Not available in new hook
  const validationErrors: string[] = []; // Not tracked separately in new hook
  const hasError = isError;
  const hasValidationErrors = false; // Not tracked separately in new hook
  const isLessonFullyCompleted = isCompleted;
  // Extract actual score from last answer's punctajFinal (backend calculates cumulative score)
  const lessonPoints = answers.length > 0
    ? (answers[answers.length - 1].punctajFinal ?? 0)
    : 0;
  const startLesson = hookResult.start;
  const updateSlideAnswer = updateAnswer;
  const goToPreviousSlide = previousSlide;

  // Practice mode detection - Use attemptState as single source of truth
  // Practice mode = student retaking already-completed lesson (not preview!)
  const isPracticeMode = React.useMemo(() => {
    if (isPreview) return false;
    if (!lesson) return false;

    const attemptState = (lesson as any).attemptState;
    return attemptState === 'APPROVED' || attemptState === 'COMPLETED';
  }, [lesson, isPreview]);

  // Auto-start ONLY for preview mode (professors reviewing lessons)
  // Students will see StartScreen with resume message, then click to start
  React.useEffect(() => {
    // Preview mode: Auto-start (skip StartScreen for professors reviewing lessons)
    if (isPreview && isReady && !isLoading) {
      start();
      return;
    }

    // Students (non-preview): Will see StartScreen with resume info
    // They must click "Incepe lectia" or "Continuă lecția" to start
  }, [isReady, isLoading, start, isPreview]);

  // Mod focus: cât timp elevul răspunde, navigația globală (hamburgerul) dispare —
  // ieșirea e ✕ din LessonTopBar. Nu pe start/final/eroare, unde meniul e util.
  const isAnswering = !isLoading && !isError && !!lesson && !isCompleted && !isReady;
  React.useEffect(() => {
    if (!isAnswering) return;
    document.body.dataset.lessonFocus = 'true';
    return () => {
      delete document.body.dataset.lessonFocus;
    };
  }, [isAnswering]);

  // Memoize adapter functions to prevent re-renders
  const memoStartLesson = useCallback(() => startLesson(), [startLesson]);
  const memoUpdateSlideAnswer = useCallback((data: any) => updateSlideAnswer(data), [updateSlideAnswer]);
  const memoGoToPreviousSlide = useCallback(() => goToPreviousSlide(), [goToPreviousSlide]);

  // I4 (final-review.md): ✕ ramane activ si in timpul trimiterii — R8 cere sa nu existe NICIODATA
  // zero iesiri, iar `exitDisabled={isSubmitting}` combinat cu focus mode (hamburger ascuns)
  // lasa elevul blocat pe o conexiune lenta/picata pana se termina request-ul. Invariantul asta
  // se pastreaza: iesirea e in continuare disponibila oricand, doar confirmarea si-a schimbat forma.
  //
  // Ce s-a schimbat: I4 argumenta ca `window.confirm` e acceptabil fiindca il declanseaza un tap
  // explicit al utilizatorului, si ca regula de proiect ar interzice casetele native doar in
  // sesiuni automatizate. Prima parte ramane adevarata, a doua nu: caseta nativa nu respecta
  // tokenii si pragul de 44px, nu exista in DOM (deci niciun ecran nu o poate verifica) si
  // blocheaza firul de executie. Nu e o regula despre automatizare, e una despre randare — de
  // aceea aceeasi inlocuire s-a facut si in `SchoolRequestsReportPage`, si in `use-lesson-listing`.
  const iesireInTimpulTrimiterii = useConfirmationDialog({ onConfirm: () => handleExit() });

  // `useConfirmationDialog` intoarce un OBIECT NOU la fiecare randare, deci a-l
  // pune intreg in deps face `useCallback` sa nu memoizeze nimic (gasit de S1).
  // Extragerea functiei NU e suficienta singura: `show` era si el recreat la
  // fiecare randare — de aceea hook-ul il stabilizeaza acum cu `useCallback`.
  const arataDialogIesire = iesireInTimpulTrimiterii.show;

  const handleExitWhileAnswering = useCallback(() => {
    if (isSubmitting) {
      arataDialogIesire();
      return;
    }
    handleExit();
  }, [isSubmitting, handleExit, arataDialogIesire]);

  // Loading state
  if (isLoading) {
    return (
      <div className="lesson-page-container flex items-center justify-center">
        <LoadingSpinner size="lg" message="Se încarcă lecția..." />
      </div>
    );
  }

  // Error state
  if (hasError || !lesson) {
    return (
      <div className="lesson-page-container flex items-center justify-center">
        <ErrorState
          message="Nu s-a putut încărca lecția"
          details="Verifică conexiunea la internet și încearcă din nou"
          onRetry={restart}
        />
      </div>
    );
  }

  // Completion screen
  if (isCompleted || isLessonFullyCompleted) {
    return (
      <div className="lesson-page-container">
        <div className="flex items-center justify-center p-4 h-full w-full">
          <CompletionScreen
            lesson={lesson}
            config={config!}
            slideAnswers={slideAnswers}
            lessonAnswer={lessonAnswer || undefined}
            totalPoints={lessonPoints}
            isPracticeMode={isPracticeMode}
            /* SP-05a: avertizarea a fost MUTATA aici de pe `EcranClase`, unde era
               nerandabila — vezi `clase-avertizare.ts`. `aTrimisAcum` o tine
               inchisa la reintrarea pe o lectie deja completata. */
            claseDejaPunctate={claseDejaPunctate}
            aTrimisAcum={aTrimisAcum}
          />
        </div>
      </div>
    );
  }

  // Start screen
  if (isReady) {
    return (
      <div className="lesson-page-container">
        <div className="flex items-center p-4 gap-4 h-full w-full">
          <StartScreen
            lesson={lesson}
            config={config!}
            onStart={memoStartLesson}
            isResumed={isResumed}
            completedSlides={answers.length}
            totalSlides={lesson?.slides?.length || 0}
          />
        </div>
      </div>
    );
  }

  // Get current slide plugin
  const plugin = currentSlide ? slideRegistry.get(currentSlide.type!) : null;
  // CLASS_EXERCISE nu mai adauga un pas: numarul de participanti e pe pagina cu pozele, iar
  // ecranul separat apare doar ca plasa la un PATCH esuat — atunci contorul ramane pe ultimul.
  const totalSlides = (lesson?.slides?.length || 0)
    // SP-05a: ecranul de clase e un pas, deci intra in „x din y" ca oricare altul.
    + (hookResult.isPracticalActivity ? 1 : 0);
  const currentStep = Math.min(currentSlideIndex + 1, totalSlides);

  // Allow participants slide to bypass plugin check (virtual slide, no plugin needed)
  // Ecranele virtuale (participanti, clase) n-au plugin si n-au slide real.
  if ((!plugin || !currentSlide) && !isParticipantsSlide && !isClaseSlide) {
    return (
      // Bara ramane montata si pe ramura asta: registerAllPlugins() e async (444-790ms masurat),
      // deci acest ecran tranzitoriu chiar apare in productie. Fara ea, isAnswering ascunde
      // hamburgerul si elevul ramane fara nicio iesire din lectie (incalca R8).
      <div className="lesson-page-container flex flex-col overflow-hidden">
        <div className="flex-none">
          <LessonTopBar current={currentStep} total={totalSlides} exitDisabled={false} onExit={handleExit} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          {/* Plugin-urile inca se incarca: asteptare, nu eroare. `pluginsReady` se schimba la
              final si re-randeaza playerul, care gaseste atunci plugin-ul. */}
          {!pluginsReady ? (
            <LoadingSpinner size="lg" message="Se încarcă lecția..." />
          ) : (
            <ErrorState
              message="Slide invalid"
              details="Nu s-a putut încărca conținutul acestui slide"
              onRetry={restart}
            />
          )}
        </div>
      </div>
    );
  }

  // Main answering view with classroom background
  return (
    <BgContentWrap transparent useScrollArea={false} customStyles="flex flex-col overflow-hidden bg-[url('/quiz-bg.jpg')] bg-cover bg-center">
      {/* SECTION 0: bara de sus — ✕ + progres + contor (wireframe 4.1) */}
      <div className="flex-none">
        <LessonTopBar current={currentStep} total={totalSlides} exitDisabled={false} onExit={handleExitWhileAnswering} />
      </div>

      {/* SECTION 1: Preview Mode Indicator */}
      {isPreview && (
        // Banda plina sub bara de sus, pe toata latimea — fara colturi rotunjite si fara bordura
        // groasa pe o singura latura (semnalate la review-ul de design, 2026-10-22).
        <div className="flex-none px-4 py-2 bg-lilac border-b border-border/30">
          <p className="max-w-4xl mx-auto text-sm text-foreground font-semibold text-center">
            Mod previzualizare – răspunsurile nu vor fi salvate
          </p>
        </div>
      )}

      {/* SECTION 2: Lesson Content (scrollable middle) — singurul scroll de pe ecran (R4) */}
      <div className="flex-1 overflow-y-auto ecotic-table-scrollbar">
        <div className="p-4">
          {isPracticeMode && (
            // Card plin (nu translucid peste fundalul ilustrat), bordura subtire uniforma; accentul
            // il poarta iconita, nu o bordura groasa pe stanga.
            <div className="mb-4 p-4 bg-card border border-lilac rounded-lg">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-lilac/40">
                  <RefreshCw className="w-5 h-5 text-magenta" aria-hidden="true" />
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-foreground">Mod exersare</p>
                  <p className="text-sm text-foreground/80">
                    Răspunsurile tale nu sunt salvate. Această lecție a fost deja completată.
                  </p>
                </div>
              </div>
            </div>
          )}

          {isClaseSlide ? (
            <EcranClase
              value={clase}
              onChange={setClase}
              onSubmit={submitCurrentAnswer}
              isSubmitting={isSubmitting}
              fragmenteRespinse={fragmenteRespinse}
            />
          ) : isParticipantsSlide ? (
            <ParticipantsSlide
              value={participants}
              onChange={setParticipants}
              onSubmit={submitCurrentAnswer}
              isSubmitting={isSubmitting}
            />
          ) : plugin && currentSlide ? (
            <>
              <plugin.CompletionComponent
                slide={currentSlide}
                answer={currentAnswer}
                onAnswer={memoUpdateSlideAnswer}
                isSubmitting={isSubmitting}
              />
              {/* CLASS_EXERCISE: numarul de participanti pe ACEEASI pagina cu pozele, ca sa
                  plece odata cu „Trimite raspuns" (sesizarea ECOTIC, 2026-10-22). Aceeasi latime
                  ca `ImageUploadCompletion` (max-w-2xl), deci se citeste ca parte din formular. */}
              {campParticipantiPePagina && (
                <div className="max-w-2xl mx-auto mt-6 p-4 bg-card border border-border rounded-lg">
                  <CampParticipanti
                    value={participants}
                    onChange={setParticipants}
                    disabled={isSubmitting}
                    incercatTrimiterea={participantiIncercat}
                  />
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* SECTION 3: Navigation (fixed bottom) — progresul s-a mutat sus */}
      <div className="flex-none border-t border-border bg-white/85 safe-area-bottom">
        <div className="p-2">
          {/* 🔴 Pe ecranul de clase, bara de navigare NU se randeaza.
              Motivul e concret: butonul ei «Finalizează» apare DEZACTIVAT langa «Trimite spre
              aprobare», care e activ — doua butoane de „termina" pe acelasi ecran, unul gri, si
              omul se intreaba de ce nu poate termina. Trimiterea de aici e a ecranului, nu a
              barei. (Semnalat de S1 masurand fluxul.)
              ⚠️ Conditia e `isClaseSlide`, care e fals cand flagul e oprit si pentru toate
              celelalte tipuri de lectie — bara ramane exact cum e azi peste tot altundeva.
              La fel pe ecranul-plasa de participanti: are propriul «Salvează numărul», iar
              «Trimite răspuns» de dedesubt ar fi al doilea buton de trimitere (vazut la 360px). */}
          {!isClaseSlide && !isParticipantsSlide && (
          <NavigationBar
            canSubmit={!hasValidationErrors}
            canSkip={false}
            canGoBack={canGoPrevious}
            isFirstSlide={isFirstSlide}
            isLastSlide={isLastSlide}
            isSubmitting={isSubmitting}
            // P1-14: eticheta pe slide-urile fara ultimul depinde de ce CERE
            // slide-ul, nu de pozitia lui — citit din capabilities-ul plugin-ului
            // (isScoreable: question; requiresUpload: image-upload), nu presupus
            // dupa tip. info/text/video n-au niciuna din cele doua -> "Mai departe".
            requiresAnswer={Boolean(plugin?.capabilities.isScoreable || plugin?.capabilities.requiresUpload)}
            validationErrors={validationErrors}
            onSubmit={submitCurrentAnswer}
            onPrevious={canGoPrevious ? memoGoToPreviousSlide : undefined}
          />
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={iesireInTimpulTrimiterii.isOpen}
        onClose={iesireInTimpulTrimiterii.handleCancel}
        onConfirm={iesireInTimpulTrimiterii.handleConfirm}
        title="Ieși din lecție?"
        description="Răspunsul se trimite. Dacă ieși acum, s-ar putea să nu fie salvat."
        confirmText="Ieși oricum"
        cancelText="Rămâi"
        variant="destructive"
      />
    </BgContentWrap>
  );
};

export default LessonPlayer;

